import { CheckCircleIcon, XMarkIcon } from '@heroicons/react/20/solid';
import cleanDeep from 'clean-deep';
import { addYears, format, parse, subYears } from 'date-fns';
import _ from 'lodash';
import React, { useEffect, useState } from 'react';
import ReactDatePicker from 'react-datepicker';
import { NotificationManager } from 'react-notifications';
import { connect } from 'react-redux';
import { Actions } from '../../../store/actions/employeeActions';
import { handleRawDate } from '../../common/DateComponents';
import Spinner from '../../common/Spinner';

const AddChildModal = ({
    child,
    closeModal,
    modalOpen,
    saveItem,
    saving,
    disabled,
    children,
    jurisdiction
}) => {

    const [data, setData] = useState({})

    const updateField = (field, value) => {
        if (field.includes('[')) field = _.setWith({}, field, value, Object)
        const newData = _.merge(data, { ...(typeof field == 'object' ? field : { [field]: value }) })
        setData({ ...newData })
    }

    useEffect(() => {
        if (!modalOpen || saving) return
        const hasChild = Object.keys(cleanDeep({ ...child }))?.length > 0
        if (!hasChild) {
            setData({
                reference: `Child #${children}`
            })
        } else {
            setData({ ...child })
        }
    }, [child, modalOpen])

    if (!modalOpen) return null

    const submit = () => {
        const hasReference = data.reference.replaceAll(/\s/g, '')
        const hasBirthFields = !data.adopted ? data.dateOfBirth || data.expectedDate : true
        const hasAdoptedFields = data.adopted ? !!data.placementDate : true
        const hasCustodyFields = data.doesNotHaveCustody ? !!data.custodyLostDate : true
        const hasPassedFields = data.deceased ? !!data.deceasedDate : true

        if (!hasReference || (!hasBirthFields || !hasAdoptedFields) || !hasCustodyFields || !hasPassedFields) {
            NotificationManager.error('Please fill all the required fields*', 'Saving Child')
            return
        }
        //* Clean data object
        if (data.adopted) data.expectedDate = undefined

        saveItem(data)
    }

    return (
        <div className="modal" tabIndex="-1" role="dialog" aria-hidden="true" style={{ display: "block", background: "rgba(0,0,0,0.4)", overflow: "auto" }}>
            <div className="modal-dialog" style={{ width: "50%" }}>
                <div className="modal-content">
                    <div className="block block-themed block-transparent remove-margin-b" style={{ padding: 0 }}>
                        <div className="block-header bg-primary-dark" style={{ padding: 20, position: 'sticky', top: 0, zIndex: 50 }}>
                            <ul className="block-options">
                                <li>
                                    <button type="button" onClick={closeModal}><XMarkIcon className="h-6 w-6 mr-3 text-white" /></button>
                                </li>
                            </ul>
                            <h3 className="block-title">{data?.id ? `Edit` : 'Add'} Child</h3>
                        </div>
                        <div className="block-content">
                            <div className='col-xs-12'>
                                <div className="col-xs-6">
                                    <div className='form-material'>
                                        <label>Reference*</label>
                                        <input
                                            className="form-control form-input"
                                            id="reference"
                                            name="reference"
                                            value={data.reference}
                                            onChange={(e) => {
                                                updateField(e.target.name, e.target.value)
                                            }}
                                            autoComplete='off'
                                            disabled={disabled}
                                        />
                                    </div>
                                </div>
                                <div className="col-xs-6">
                                    <div className='form-material'>
                                        <label>Date of Birth{!data.adopted ? '*' : null}</label>
                                        <ReactDatePicker
                                            disabled={disabled}
                                            dateFormat="dd MMM yyyy"
                                            className="form-control form-input"
                                            wrapperClassName="full-width"
                                            minDate={subYears(new Date(), 100)}
                                            maxDate={addYears(new Date(), 10)}
                                            selected={data.dateOfBirth ? parse(data.dateOfBirth, 'yyyy-MM-dd', new Date()) : null}
                                            autoComplete="off"
                                            onChange={(dateOfBirth,) => {
                                                updateField('dateOfBirth', format(dateOfBirth, 'yyyy-MM-dd'))
                                            }}
                                            onChangeRaw={e => handleRawDate(e.target.value, (dateOfBirth) => {
                                                updateField('dateOfBirth', format(dateOfBirth, 'yyyy-MM-dd'))
                                            })}
                                            strictParsing
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className='col-xs-12'>
                                <div className="col-xs-6">
                                    <div className="form-material" style={{ marginTop: '25px' }}>
                                        <label className="css-input switch switch-sm switch-success" style={{ cursor: disabled ? 'not-allowed' : 'pointer' }}>
                                            <input
                                                id="adopted"
                                                name="adopted"
                                                checked={data.adopted}
                                                onClick={(e) => updateField(e.target.name, !data[e.target.name])}
                                                onChange={() => { }} //To Prevent Error Spamming in Console
                                                type="checkbox"
                                                disabled={disabled}
                                            />
                                            <span></span>
                                            <label htmlFor="material-text" style={{ marginBottom: 0 }}>
                                                Adopted
                                            </label>
                                        </label>
                                        <div className="clearfix"></div>
                                    </div>
                                </div>
                                <div className="col-xs-6">
                                    {data.adopted ?
                                        <div className='form-material'>
                                            <label>Placement Date*</label>
                                            <ReactDatePicker
                                                disabled={disabled}
                                                dateFormat="dd MMM yyyy"
                                                className="form-control form-input"
                                                wrapperClassName="full-width"
                                                minDate={subYears(new Date(), 100)}
                                                maxDate={addYears(new Date(), 10)}
                                                selected={data.placementDate ? parse(data.placementDate, 'yyyy-MM-dd', new Date()) : null}
                                                autoComplete="off"
                                                onChange={(placementDate,) => {
                                                    updateField('placementDate', format(placementDate, 'yyyy-MM-dd'))
                                                }}
                                                onChangeRaw={e => handleRawDate(e.target.value, (placementDate) => {
                                                    updateField('placementDate', format(placementDate, 'yyyy-MM-dd'))
                                                })}
                                                strictParsing
                                            />
                                        </div>
                                        : null}
                                    {!data.adopted && !data.dateOfBirth ?
                                        <div className='form-material'>
                                            <label>Expected Date of Birth{!data.adopted ? '*' : null}</label>
                                            <ReactDatePicker
                                                disabled={disabled}
                                                dateFormat="dd MMM yyyy"
                                                className="form-control form-input"
                                                wrapperClassName="full-width"
                                                minDate={subYears(new Date(), 100)}
                                                maxDate={addYears(new Date(), 10)}
                                                selected={data.expectedDate ? parse(data.expectedDate, 'yyyy-MM-dd', new Date()) : null}
                                                autoComplete="off"
                                                onChange={(expectedDate,) => {
                                                    updateField('expectedDate', format(expectedDate, 'yyyy-MM-dd'))
                                                }}
                                                onChangeRaw={e => handleRawDate(e.target.value, (expectedDate) => {
                                                    updateField('expectedDate', format(expectedDate, 'yyyy-MM-dd'))
                                                })}
                                                strictParsing
                                            />
                                        </div>
                                        : null}
                                </div>
                            </div>
                            {data?.id ?
                                <>
                                    <div className='col-xs-12'>
                                        <div className="col-xs-6">
                                            <div className="form-material" style={{ marginTop: '25px' }}>
                                                <label className="css-input switch switch-sm switch-success" style={{ cursor: disabled ? 'not-allowed' : 'pointer' }}>
                                                    <input
                                                        id="doesNotHaveCustody"
                                                        name="doesNotHaveCustody"
                                                        checked={!data.doesNotHaveCustody}
                                                        onClick={(e) => updateField(e.target.name, !data[e.target.name])}
                                                        onChange={() => { }} //To Prevent Error Spamming in Console
                                                        type="checkbox"
                                                        disabled={disabled}
                                                    />
                                                    <span></span>
                                                    <label htmlFor="material-text" style={{ marginBottom: 0 }}>
                                                        Has Custody
                                                    </label>
                                                </label>
                                                <div className="clearfix"></div>
                                            </div>
                                        </div>
                                        {data.doesNotHaveCustody ?
                                            <div className="col-xs-6">
                                                <div className='form-material'>
                                                    <label>Date of Loss of Custody*</label>
                                                    <ReactDatePicker
                                                        disabled={disabled}
                                                        dateFormat="dd MMM yyyy"
                                                        className="form-control form-input"
                                                        wrapperClassName="full-width"
                                                        minDate={subYears(new Date(), 100)}
                                                        maxDate={addYears(new Date(), 10)}
                                                        selected={data.custodyLostDate ? parse(data.custodyLostDate, 'yyyy-MM-dd', new Date()) : null}
                                                        autoComplete="off"
                                                        onChange={(custodyLostDate,) => {
                                                            updateField('custodyLostDate', format(custodyLostDate, 'yyyy-MM-dd'))
                                                        }}
                                                        onChangeRaw={e => handleRawDate(e.target.value, (custodyLostDate) => {
                                                            updateField('custodyLostDate', format(custodyLostDate, 'yyyy-MM-dd'))
                                                        })}
                                                        strictParsing
                                                    />
                                                </div>
                                            </div>
                                            : null}
                                    </div>
                                    <div className='col-xs-12'>
                                        <div className="col-xs-6">
                                            <div className="form-material" style={{ marginTop: '25px' }}>
                                                <label className="css-input switch switch-sm switch-success" style={{ cursor: disabled ? 'not-allowed' : 'pointer' }}>
                                                    <input
                                                        id="deceased"
                                                        name="deceased"
                                                        checked={data.deceased}
                                                        onClick={(e) => updateField(e.target.name, !data[e.target.name])}
                                                        onChange={() => { }} //To Prevent Error Spamming in Console
                                                        type="checkbox"
                                                        disabled={disabled}
                                                    />
                                                    <span></span>
                                                    <label htmlFor="material-text" style={{ marginBottom: 0 }}>
                                                        Deceased {!data.adopted ? '/ Miscarriage' : null}
                                                    </label>
                                                </label>
                                                <div className="clearfix"></div>
                                            </div>
                                        </div>
                                        {data.deceased ?
                                            <div className="col-xs-6">
                                                <div className='form-material'>
                                                    <label>Date of Passing*</label>
                                                    <ReactDatePicker
                                                        disabled={disabled}
                                                        dateFormat="dd MMM yyyy"
                                                        className="form-control form-input"
                                                        wrapperClassName="full-width"
                                                        minDate={subYears(new Date(), 100)}
                                                        maxDate={addYears(new Date(), 10)}
                                                        selected={data.deceasedDate ? parse(data.deceasedDate, 'yyyy-MM-dd', new Date()) : null}
                                                        autoComplete="off"
                                                        onChange={(deceasedDate,) => {
                                                            updateField('deceasedDate', format(deceasedDate, 'yyyy-MM-dd'))
                                                        }}
                                                        onChangeRaw={e => handleRawDate(e.target.value, (deceasedDate) => {
                                                            updateField('deceasedDate', format(deceasedDate, 'yyyy-MM-dd'))
                                                        })}
                                                        strictParsing
                                                    />
                                                </div>
                                            </div>
                                            : null}
                                    </div>
                                </>
                                : null}
                            <div className='col-xs-12 form-container'>
                                <button
                                    className='btn btn-success pull-right'
                                    disabled={saving || disabled}
                                    onClick={() => { if (!saving) submit() }}
                                >
                                    {saving ? <>
                                        <Spinner
                                            style={{ width: '1.5rem', height: '1.5rem', marginRight: 5 }}
                                            className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" />
                                        <div style={{ float: "left" }}>Saving...</div>
                                    </> : <>
                                        <CheckCircleIcon style={{ height: "1.5em", float: "left", marginRight: 5 }} />
                                        <div style={{ float: "left" }}>{data.id ? 'Update' : 'Add'} Child</div>
                                    </>}
                                </button>
                            </div>
                            <div className='clearfix' />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

const mapStateToProps = (state, ownProps) => {
    return {
        jurisdiction: state.company?.settings?.jurisdiction,
        child: state.employee?.children?.child,
        modalOpen: state.employee?.children?.childrenModal,
        children: (state.employee?.children?.list || [])?.length + 1,
        saving: state.employee?.children?.saving,
        disabled: (state?.permissions?.my?.permissions?.people == "Read"),
    };
};

const mapDispatchToProps = (dispatch, ownProps) => ({
    closeModal: () => dispatch(Actions.toggleChildrenModal({ open: false })),
    saveItem: (item) => dispatch(Actions.saveChild({ item }))
});

export default connect(mapStateToProps, mapDispatchToProps)(AddChildModal);
